import type { FormAnswerError, FormAnswers, FormField, FormFieldType, FormResponseStatus, FormStatus } from '@/lib/api';

export const FIELD_TYPE_LABELS: Record<FormFieldType, string> = {
  short_text: 'Short text',
  long_text: 'Paragraph',
  number: 'Number',
  email: 'Email',
  url: 'Link',
  single_choice: 'Single choice',
  multiple_choice: 'Multiple choice',
  yes_no: 'Yes / No',
  date: 'Date',
  section: 'Section heading',
  file: 'File upload',
};

export const FIELD_TYPE_ORDER: FormFieldType[] = [
  'short_text',
  'long_text',
  'number',
  'email',
  'url',
  'single_choice',
  'multiple_choice',
  'yes_no',
  'date',
  'file',
  'section',
];

export const FORM_STATUS_LABELS: Record<FormStatus, string> = {
  draft: 'Draft',
  published: 'Published',
  archived: 'Archived',
};

export const FORM_STATUS_CLASSES: Record<FormStatus, string> = {
  draft: 'bg-grey-100 text-grey-600',
  published: 'bg-success/10 text-success',
  archived: 'bg-grey-100 text-grey-400',
};

export const RESPONSE_STATUS_LABELS: Record<FormResponseStatus, string> = {
  pending: 'Not started',
  draft: 'In progress',
  submitted: 'Submitted',
};

export const RESPONSE_STATUS_CLASSES: Record<FormResponseStatus, string> = {
  pending: 'bg-grey-100 text-grey-600',
  draft: 'bg-warning/10 text-warning',
  submitted: 'bg-success/10 text-success',
};

export function newFieldId(): string {
  return `f_${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36).slice(-4)}`;
}

export function isBlank(v: unknown): boolean {
  return v === undefined || v === null || v === '' || (Array.isArray(v) && v.length === 0);
}

/** Client-side mirror of the server's strict check: required + cheap shape rules. */
export function validateAnswersClient(fields: FormField[], answers: FormAnswers): FormAnswerError[] {
  const errors: FormAnswerError[] = [];
  for (const f of fields) {
    if (f.type === 'section') continue;
    const v = answers[f.id];
    if (isBlank(v)) {
      if (f.required) errors.push({ fieldId: f.id, message: 'This field is required' });
      continue;
    }
    if (f.type === 'email' && typeof v === 'string' && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim())) {
      errors.push({ fieldId: f.id, message: 'Must be a valid email address' });
    }
    if (f.type === 'url' && typeof v === 'string') {
      try {
        const u = new URL(v.trim());
        if (!['http:', 'https:'].includes(u.protocol)) throw new Error();
      } catch {
        errors.push({ fieldId: f.id, message: 'Must be a valid link (https://…)' });
      }
    }
  }
  return errors;
}

export function formatDueDate(dateString: string): { text: string; isOverdue: boolean; isDueSoon: boolean } {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(dateString);
  due.setHours(0, 0, 0, 0);
  const diffDays = Math.ceil((due.getTime() - today.getTime()) / 86_400_000);
  if (diffDays < 0) {
    const n = Math.abs(diffDays);
    return { text: `${n} day${n !== 1 ? 's' : ''} overdue`, isOverdue: true, isDueSoon: false };
  }
  if (diffDays === 0) return { text: 'Due today', isOverdue: false, isDueSoon: true };
  if (diffDays === 1) return { text: 'Due tomorrow', isOverdue: false, isDueSoon: true };
  if (diffDays <= 7) return { text: `Due in ${diffDays} days`, isOverdue: false, isDueSoon: true };
  return { text: `Due ${due.toLocaleDateString()}`, isOverdue: false, isDueSoon: false };
}

export function formatDateShort(value?: string | null): string {
  if (!value) return '—';
  return new Date(value).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' });
}

/** Plain-text rendering of an answer for tables and read-only views. */
export function formatAnswer(field: FormField, value: unknown, files?: Map<string, string>): string {
  if (isBlank(value)) return '—';
  switch (field.type) {
    case 'yes_no':
      return value ? 'Yes' : 'No';
    case 'multiple_choice':
      return Array.isArray(value) ? value.join(', ') : String(value);
    case 'file':
      return Array.isArray(value) ? value.map((id) => files?.get(String(id)) ?? 'File').join(', ') : String(value);
    case 'date':
      return typeof value === 'string' ? new Date(`${value}T00:00:00`).toLocaleDateString() : String(value);
    default:
      return String(value);
  }
}
